import { useState, useEffect } from 'react';
import { api, formatCurrency } from '../../lib/api';
import { Mail, MailCheck, Download, Loader2, CheckCircle, FileText, RefreshCw, Palmtree, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BulletinsPaieSection = ({ onImported }) => {
  const [gmailStatus, setGmailStatus] = useState(null);
  const [emails, setEmails] = useState([]);
  const [bulletins, setBulletins] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState({});
  const [showBulletins, setShowBulletins] = useState(false);

  useEffect(() => {
    checkGmail();
    loadBulletins();
  }, []);

  const checkGmail = async () => {
    try {
      const res = await api.get('/oauth/gmail/status');
      setGmailStatus(res.data);
      if (res.data.connected) scanPaie();
      else setLoading(false);
    } catch { setLoading(false); }
  };

  const loadBulletins = async () => {
    try {
      const res = await api.get('/paie/bulletins');
      setBulletins(res.data.bulletins || []);
    } catch {}
  };

  const scanPaie = async () => {
    setScanning(true);
    try {
      const res = await api.get('/paie/scan-gmail?max_results=30');
      setEmails(res.data.emails || []);
    } catch { toast.error('Erreur scan bulletins'); }
    setScanning(false);
    setLoading(false);
  };

  const importBulletin = async (gmailId) => {
    setImporting(prev => ({ ...prev, [gmailId]: true }));
    try {
      const res = await api.post(`/paie/import/${gmailId}`);
      if (res.data.error) {
        toast.error(res.data.duplicate ? 'Doublon — déjà importé' : res.data.error);
      } else {
        const nb = res.data.nb_imported;
        const dups = res.data.nb_duplicates;
        let msg = `${nb} bulletin${nb > 1 ? 's' : ''} importé${nb > 1 ? 's' : ''}`;
        if (dups > 0) msg += ` · ${dups} doublon${dups > 1 ? 's' : ''}`;
        toast.success(msg);
        // Show details
        for (const r of (res.data.results || [])) {
          if (r.status === 'importé') {
            toast.success(`${r.employe} — ${formatCurrency(r.net)} net · ${r.conges_restants}j congés`, { duration: 5000 });
          }
        }
        setEmails(prev => prev.map(e => e.gmail_id === gmailId ? { ...e, already_imported: true } : e));
        loadBulletins();
        if (onImported) onImported();
      }
    } catch { toast.error('Erreur import bulletin'); }
    setImporting(prev => ({ ...prev, [gmailId]: false }));
  };

  const extractSender = (from) => {
    if (!from) return '';
    const match = from.match(/^"?([^"<]+)"?\s*</);
    if (match) return match[1].trim();
    return from.split('@')[0].replace(/[._]/g, ' ');
  };

  const formatEmailDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;
    } catch { return ''; }
  };

  if (!gmailStatus?.connected) {
    return (
      <div className="lm-card p-4" data-testid="paie-gmail-not-connected">
        <div className="flex items-center gap-3">
          <Mail size={18} className="text-[rgb(var(--lm-text-muted))]" />
          <div>
            <p className="text-sm text-[rgb(var(--lm-text))]">Bulletins de paie Gmail</p>
            <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Connectez Gmail dans Factures pour scanner les bulletins automatiquement</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="paie-gmail-section">
      {/* Header */}
      <div className="lm-card p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MailCheck size={16} className="text-[rgb(var(--lm-green))]" />
          <span className="text-xs text-[rgb(var(--lm-text-secondary))]">
            Bulletins de paie
            <span className="text-[rgb(var(--lm-text-muted))] ml-1">({bulletins.length} importés)</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={scanPaie} disabled={scanning}
            className="flex items-center gap-1 px-2 py-1 rounded text-[10px] bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]"
            data-testid="scan-bulletins-btn">
            {scanning ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
            Scanner
          </button>
          {bulletins.length > 0 && (
            <button onClick={() => setShowBulletins(!showBulletins)}
              className="flex items-center gap-1 px-2 py-1 rounded text-[10px] bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))]"
              data-testid="toggle-bulletins-btn">
              <FileText size={10} /> Historique
              {showBulletins ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
            </button>
          )}
        </div>
      </div>

      {/* Emails with bulletins */}
      {emails.length > 0 && (
        <div className="lm-card overflow-hidden divide-y divide-[rgb(var(--lm-border))]">
          <div className="px-4 py-2 bg-[rgb(var(--lm-bg)/0.5)]">
            <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">
              {emails.length} email{emails.length > 1 ? 's' : ''} avec bulletins de paie
            </p>
          </div>
          {emails.map((email, i) => (
            <div key={email.gmail_id} className="px-4 py-2.5 flex items-center justify-between hover:bg-[rgb(var(--lm-bg)/0.3)]"
              data-testid={`paie-email-${i}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[rgb(var(--lm-text))] truncate">{extractSender(email.from)}</p>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))] truncate">{email.subject}</p>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{formatEmailDate(email.date)}</p>
              </div>
              <div className="shrink-0 ml-3">
                {email.already_imported ? (
                  <span className="flex items-center gap-1 text-[10px] text-[rgb(var(--lm-green))] px-2 py-1 rounded bg-[rgb(var(--lm-green)/0.1)]">
                    <CheckCircle size={10} /> Importé
                  </span>
                ) : (
                  <button onClick={() => importBulletin(email.gmail_id)} disabled={importing[email.gmail_id]}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 text-[10px] font-medium hover:bg-blue-500/20 disabled:opacity-50"
                    data-testid={`import-bulletin-btn-${i}`}>
                    {importing[email.gmail_id] ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />}
                    Importer
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Imported bulletins history */}
      {showBulletins && bulletins.length > 0 && (
        <div className="lm-card overflow-hidden divide-y divide-[rgb(var(--lm-border))]">
          <div className="px-4 py-2 bg-[rgb(var(--lm-bg)/0.5)]">
            <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">
              Bulletins importés ({bulletins.length})
            </p>
          </div>
          {bulletins.map((b, i) => (
            <div key={i} className="px-4 py-2.5 flex items-center justify-between hover:bg-[rgb(var(--lm-bg)/0.3)]">
              <div>
                <p className="text-sm text-[rgb(var(--lm-text))]">{b.nom_employe}</p>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{b.periode} · {b.filename}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-sm text-[rgb(var(--lm-text))]">{formatCurrency(b.salaire_net)} net</p>
                {b.conges_restants > 0 && (
                  <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{b.conges_restants}j congés</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const CongesSection = () => {
  const [conges, setConges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConges();
  }, []);

  const loadConges = async () => {
    try {
      const res = await api.get('/paie/conges');
      setConges(res.data.employes || []);
    } catch {}
    setLoading(false);
  };

  const totalRestants = conges.reduce((s, c) => s + (c.conges_restants || 0), 0);
  const hasData = conges.some(c => c.conges_acquis > 0 || c.conges_restants > 0);

  if (!hasData && !loading) return null;

  return (
    <div className="lm-card p-4 space-y-3" data-testid="conges-section">
      <div className="flex items-center justify-between">
        <h3 className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] flex items-center gap-1.5">
          <Palmtree size={10} /> Congés équipe
        </h3>
        <span className="font-mono text-xs text-lemas-gold">{totalRestants}j restants</span>
      </div>

      <div className="divide-y divide-[rgb(var(--lm-border)/0.5)]">
        {conges.map((c, i) => (
          <div key={i} className="flex items-center justify-between py-2" data-testid={`conge-row-${i}`}>
            <div>
              <p className="text-sm text-[rgb(var(--lm-text))]">{c.nom}</p>
              {c.derniere_periode && (
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Maj: {c.derniere_periode}</p>
              )}
            </div>
            <div className="flex items-center gap-3 text-right">
              <div>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Acquis</p>
                <p className="font-mono text-xs text-[rgb(var(--lm-text-secondary))]">{c.conges_acquis}j</p>
              </div>
              <div>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Pris</p>
                <p className="font-mono text-xs text-[rgb(var(--lm-text-secondary))]">{c.conges_pris}j</p>
              </div>
              <div>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Restants</p>
                <p className={`font-mono text-sm font-medium ${c.conges_restants > 20 ? 'text-[rgb(var(--lm-red))]' : c.conges_restants > 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-text-secondary))]'}`}>
                  {c.conges_restants}j
                </p>
              </div>
              {c.conges_restants > 20 && (
                <AlertTriangle size={14} className="text-[rgb(var(--lm-red))]" title="Beaucoup de congés à poser" />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
